import { formatDecimal, parseDecimal } from '@k-management/shared';
import { type InputHTMLAttributes, useState } from 'react';

/**
 * Number field (decision of 26/09/2026): the user types the decimals with a
 * dot or a comma and never the thousands. While the field has the focus it
 * shows the number without the thousands dot («15432,70»), so that what is
 * typed is read back the same way; away from it, the Italian format
 * («15.432,70»). `value` is what was typed, or what came from the API
 * («15432.70»): both are read by the rules of `parseDecimal`.
 */
export function DecimalInput({
  value,
  onChange,
  decimals,
  ...rest
}: {
  value: string;
  onChange: (value: string) => void;
  /** Decimals shown away from the field: `[min, max]`, e.g. `[2, 2]` for money. */
  decimals: [number, number];
} & Omit<InputHTMLAttributes<HTMLInputElement>, 'value' | 'onChange' | 'type'>) {
  const [focused, setFocused] = useState(false);
  const number = value.trim() === '' ? null : parseDecimal(value);
  const [min, max] = decimals;
  const shown = number === null ? value : formatDecimal(number, focused ? 0 : min, max, !focused);

  return (
    <input
      {...rest}
      type="text"
      inputMode="decimal"
      value={focused ? value : shown}
      onFocus={(event) => {
        setFocused(true);
        // Show the number as it will be typed: «15432,7», not «15432.70».
        if (number !== null) onChange(formatDecimal(number, 0, max, false));
        rest.onFocus?.(event);
      }}
      onBlur={(event) => {
        setFocused(false);
        rest.onBlur?.(event);
      }}
      onChange={(event) => onChange(event.target.value)}
    />
  );
}
